import { DECLARATION, RULESET } from 'stylis';
import { assign, concat, escapeRegExp } from '../../../util';
export function mergeStylePlugin(target, source) {
    const { conditions, declarations, properties } = source;
    if (conditions) {
        if (!target.conditions) {
            target.conditions = {};
        }
        assign(target.conditions, conditions);
    }
    if (declarations) {
        if (!target.declarations) {
            target.declarations = [];
        }
        concat(target.declarations, declarations);
    }
    if (properties) {
        if (!target.properties) {
            target.properties = {};
        }
        assign(target.properties, properties);
    }
}
export function mergeStylePlugins(plugins) {
    const target = {};
    for(let i = 0; i < plugins.length; i++){
        mergeStylePlugin(target, plugins[i]);
    }
    return target;
}
export function cloneDeclaration(element, props = element.props, children = element.children) {
    return {
        ...element,
        data: undefined,
        type: DECLARATION,
        props,
        children,
        value: children ? `${props}:${children};` : '',
        length: props.length
    };
}
export function cloneDeclarations(element, declarations) {
    return declarations.map((decl)=>({
            ...decl,
            parent: element.type === DECLARATION ? element.parent : element,
            root: element.type === DECLARATION ? element.root : element
        }));
}
export function matchOutsideParens(value, input, callback) {
    const source = typeof input === 'string' ? escapeRegExp(input) : input.source;
    const withParens = new RegExp(`[()]|${source}`, 'gi');
    let depth = 0;
    return value.replace(withParens, (match)=>{
        switch(match){
            case '(':
                depth += 1;
                return match;
            case ')':
                depth -= 1;
                return match;
            default:
                return callback(match, depth);
        }
    });
}
const pseudoElementRegExp = /(:(?:before|after|first-letter|first-line)|::\w+(?:[^(\w]|$))/;
function includesPseudoElement(selector) {
    return !!selector.match(pseudoElementRegExp);
}
export function nestSelectors(ancestors, nested, prefix = true) {
    const result = [];
    for(let i = 0; i < ancestors.length; i++){
        const ancestor = ancestors[i];
        const ancestorIsPseudo = includesPseudoElement(ancestor);
        for(let j = 0; j < nested.length; j++){
            const current = nested[j];
            const currentIsPseudo = includesPseudoElement(ancestor);
            if (ancestorIsPseudo && currentIsPseudo) {
                continue;
            }
            const selector = prefix && current.indexOf('&') === -1 ? `${ancestor} ${current}` : current.replace(/&[\f]?/g, ancestor);
            if (!result.includes(selector)) {
                result.push(selector);
            }
        }
    }
    return result;
}
const supportedAtRules = [
    '@media',
    '@supports'
];
function nestAtRules(element, selectors) {
    if (!supportedAtRules.includes(element.type)) {
        return;
    }
    const children = element.children;
    for(let j = 0; j < children.length; j++){
        const child = children[j];
        if (child.type === RULESET) {
            // @media rules sometimes create implicit rules that include
            // ancestor at-rules.
            if (child.value.startsWith('@')) {
                child.value = '&';
            }
            if (selectors.length) {
                child.props = nestSelectors(selectors, // We can't use child.props here because Stylis removes & characters
                // from the processed props.
                matchOutsideParens(child.value, ',', (match, depth)=>{
                    return depth === 0 ? '::split::' : match;
                }).split('::split::'));
            }
        } else {
            nestAtRules(child, selectors);
        }
    }
}
export function convertAtRuleToRuleset(element, index, elements, selectors = [
    '&'
]) {
    let parent = element.parent;
    if ((parent === null || parent === void 0 ? void 0 : parent.type) !== RULESET) {
        parent = undefined;
    }
    // Hoist any at-rules and rulesets after the current element.
    const children = element.children;
    let currentIndex = index;
    element.type = RULESET;
    element.value = selectors.join(', ');
    element.props = parent ? nestSelectors(parent.props, selectors) : [];
    element.children = [];
    for(let i = 0; i < children.length; i++){
        const child = children[i];
        if (child.type === RULESET) {
            elements.splice(++currentIndex, 0, child);
            child.root = element.root;
            if (parent) {
                child.props = nestSelectors(element.props, // We can't use child.props here because Stylis removes & characters
                // from the processed props.
                matchOutsideParens(child.value, ',', (match, depth)=>{
                    return depth === 0 ? '::split::' : match;
                }).split('::split::'));
            }
        } else if (child.type.startsWith('@')) {
            elements.splice(++currentIndex, 0, child);
            child.root = element.root;
            nestAtRules(child, element.props);
        } else {
            element.children.push(child);
        }
    }
}
export function cloneRuleset(element, props = element.props.slice(), children = element.children) {
    const ruleset = {
        ...element,
        data: undefined,
        type: RULESET,
        props,
        children,
        value: '',
        length: props.length
    };
    ruleset.children = cloneDeclarations(ruleset, children);
    return ruleset;
}
export function createAlias(target) {
    const targets = Array.isArray(target) ? target : [
        target
    ];
    return function(element) {
        const children = element.children;
        return targets.map((prop)=>cloneDeclaration(element, prop, children));
    };
}
export function getPathFromRoot(element) {
    let r = element.root;
    const path = [];
    while(r){
        path.unshift(r.value);
        r = r.root;
    }
    return path;
}
export function getStyleData(element, key) {
    if (!element.data) {
        element.data = {};
    }
    if (key) {
        if (!element.data[key]) {
            element.data[key] = {};
        }
        return element.data[key];
    }
    return element.data;
}
export function recordDeclaration(element, property, value) {
    const ruleset = element.root;
    if (!ruleset) {
        return;
    }
    const declarations = getStyleData(ruleset, 'declarations');
    declarations[property] = cloneDeclaration(element, property, value);
}
export function addRulesetAfter(element, id, selectors = [], declarations = []) {
    const ruleset = element.root;
    if (!ruleset || ruleset.type !== RULESET) {
        return;
    }
    const after = getStyleData(ruleset, 'after');
    const existing = after[id];
    if (existing) {
        const existingDeclarations = existing.children;
        existingDeclarations.push(...cloneDeclarations(existing, declarations));
    } else {
        const props = ruleset.props;
        const nestedSelectors = selectors.length ? nestSelectors(props, selectors, false) : props.slice();
        if (nestedSelectors.length) {
            after[id] = cloneRuleset(ruleset, nestedSelectors, declarations);
        }
    }
}
export function createVariableProperty(property, fallback = '') {
    const contents = `var(--${property}${fallback ? `,${fallback}` : ''})`;
    const customProperty = `--${property}`;
    return function(element) {
        const children = element.children;
        if (children === contents) {
            return;
        }
        return [
            cloneDeclaration(element, property, contents),
            cloneDeclaration(element, customProperty, children)
        ];
    };
}

//# sourceMappingURL=utils.js.map